import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { dataService } from '../services/dataService';
import { Item } from '../types';
import ItemForm from './ItemForm';
import './EditItem.css';

// Base64 encoded simple SVG placeholder  
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNODAgNjBIMTIwVjE0MEg4MFY2MFoiIGZpbGw9IiNEREQiLz48cGF0aCBkPSJNNjUgOTBIMTM1VjExMEg2NVY5MFoiIGZpbGw9IiNEREQiLz48L3N2Zz4=';

const EditItem: React.FC = () => {
  const { user } = useAuth();
  const { deleteItem, updateItem } = useData();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadItem = async () => {
      if (!itemId || !user) {
        setLoading(false);
        return;
      }

      try {
        // Get all user items and find the one we want to edit
        const userItems = await dataService.getUserItems(user.id);
        const foundItem = userItems.find((item: Item) => item.id === itemId);
        setItem(foundItem || null);
      } catch (error) {
        console.error('Error loading item:', error);
        setItem(null);
      } finally {
        setLoading(false);
      }
    };

    loadItem();
  }, [itemId, user]);

  const handleEditItem = async (partialItem: Partial<Item>) => {
    if (!item) return;
    
    // Validate location fields before updating the item
    if (!partialItem.location?.address || !partialItem.location?.city || !partialItem.location?.postcode) {
      alert('Please fill in all location fields (address, city, and postcode)');
      return;
    }
    
    // Merge the partial update with the existing item
    const updatedItem: Item = {
      ...item,
      ...partialItem,
      id: item.id, // Ensure ID doesn't change
      ownerId: item.ownerId // Ensure owner doesn't change
    };

    try {
      const success = await updateItem(updatedItem);
      if (success) {
        console.log('Item updated successfully');
        navigate('/my-items', { state: { updatedItem } });
      } else {
        alert('Failed to update item. Please check that the address is valid and try again.');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Error updating item. Please check that the address is valid and try again.');
    }
  };

  const handleCancel = () => {
    navigate('/my-items');
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      if (!item?.id) {
        alert('Item ID not found');
        return;
      }
      
      try {
        const success = await deleteItem(item.id);
        if (success) {
          console.log('Item deleted successfully');
          navigate('/my-items');
        } else {
          alert('Failed to delete item. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Error deleting item. Please try again.');
      }
    }
  };

  if (!user) {
    return <div>Please log in to edit items.</div>;
  }

  if (loading) {
    return <div>Loading item...</div>;
  }

  if (!item) {
    return (
      <div className="edit-item-page">
        <div className="edit-item-header">
          <h1>Item Not Found</h1>
          <button 
            className="back-button"
            onClick={handleCancel}
          >
            ← Back to My Items
          </button>
        </div>
        <p>The item you're trying to edit could not be found.</p>
      </div>
    );
  }

  return (
    <div className="edit-item-page">
      <div className="edit-item-header">
        <h1>Edit Item: {item.name}</h1>
        <div className="header-buttons">
          <button 
            className="delete-button"
            onClick={handleDelete}
          >
            Delete Item
          </button>
          <button 
            className="back-button"
            onClick={handleCancel}
          >
            ← Back to My Items
          </button>
        </div>
      </div>
      
      <div className="edit-item-content">
        <ItemForm 
          onSubmit={handleEditItem}
          onCancel={handleCancel}
          initialValues={item}
        />
      </div>
    </div>
  );
};

export default EditItem; 